/**
 * 요청 상태와 캐시.
 *
 * **React 를 모른다.** 사람 결정으로 Query 를 Web·Native 가 공유하기로 했는데,
 * 훅으로 공유하면 두 앱이 서로 다른 React 인스턴스를 만나 "invalid hook call" 이
 * 나는 부류의 사고가 생긴다. 그래서 여기는 순수 로직만 두고, 훅은 플랫폼마다 얇게 감싼다.
 *
 * 덕분에 이 파일은 렌더러 없이 그대로 테스트할 수 있다.
 */
/**
 * 키 하나당 상태 하나. 같은 키를 여러 화면이 구독하면 요청은 한 번만 나간다.
 */
export class QueryStore {
    #entries = new Map();
    #listeners = new Map();
    #inflight = new Map();
    #staleMs;
    #now;
    constructor(opts = {}) {
        this.#staleMs = opts.staleMs ?? 0;
        this.#now = opts.now ?? Date.now;
    }
    static key(parts) {
        return JSON.stringify(parts);
    }
    /** 계정 변경 즉시 캐시와 진행 중 응답을 폐기합니다. 플랫폼은 이 계약을 연결합니다. */
    bindSession(session) {
        let owner;
        return session.subscribe((state) => {
            const next = state.status === "authenticated" ? state.user?.id ?? null : null;
            if (owner !== next)
                this.clear();
            owner = next;
        });
    }
    get(key) {
        return (this.#entries.get(key)?.state ?? { status: "idle" });
    }
    updatedAt(key) {
        return this.#entries.get(key)?.updatedAt ?? 0;
    }
    subscribe(key, fn) {
        let set = this.#listeners.get(key);
        if (!set) {
            set = new Set();
            this.#listeners.set(key, set);
        }
        set.add(fn);
        return () => { set.delete(fn); };
    }
    #emit(key) {
        const state = this.get(key);
        for (const fn of this.#listeners.get(key) ?? [])
            fn(state);
    }
    #set(key, state, touch = false) {
        const prev = this.#entries.get(key);
        this.#entries.set(key, {
            state: state,
            updatedAt: touch ? this.#now() : prev?.updatedAt ?? 0,
            controller: prev?.controller ?? null,
        });
        this.#emit(key);
    }
    /** 캐시가 아직 쓸 만한가. */
    isFresh(key) {
        if (this.#staleMs <= 0)
            return false;
        const e = this.#entries.get(key);
        if (!e || e.state.status !== "success")
            return false;
        return this.#now() - e.updatedAt < this.#staleMs;
    }
    /**
     * 요청. 같은 키가 이미 날아가고 있으면 **새로 보내지 않고 그 결과를 함께 기다린다** —
     * 탭을 빠르게 오갈 때 같은 요청이 여러 번 나가는 것을 막는다.
     */
    async fetch(key, fn, opts = {}) {
        if (!opts.force && this.isFresh(key))
            return this.get(key);
        const running = this.#inflight.get(key);
        if (running && !opts.force) {
            await running.catch(() => undefined);
            return this.get(key);
        }
        this.#entries.get(key)?.controller?.abort();
        const controller = new AbortController();
        const prev = this.#entries.get(key);
        this.#entries.set(key, {
            state: prev?.state.status === "success" ? prev.state : { status: "loading" },
            updatedAt: prev?.updatedAt ?? 0,
            controller,
        });
        /* 이미 성공한 값이 있으면 그대로 두고 조용히 갱신한다 — 화면이 깜빡이지 않는다. */
        if (prev?.state.status !== "success")
            this.#emit(key);
        const p = fn(controller.signal)
            .then((data) => {
            if (controller.signal.aborted)
                return;
            this.#set(key, { status: "success", data }, true);
        })
            .catch((e) => {
            if (controller.signal.aborted)
                return;
            if (e instanceof Error && e.name === "AbortError")
                return;
            this.#set(key, { status: "error", error: e instanceof Error ? e : new Error(String(e)) });
        })
            .finally(() => {
            if (this.#inflight.get(key) === p)
                this.#inflight.delete(key);
        });
        this.#inflight.set(key, p);
        await p;
        return this.get(key);
    }
    /** 화면을 떠날 때. 진행 중 요청을 끊는다 (NFR-R-04). */
    cancel(key) {
        this.#entries.get(key)?.controller?.abort();
        this.#inflight.delete(key);
    }
    /** 캐시를 버린다. 로그아웃처럼 다른 사람의 데이터가 남으면 안 될 때. */
    clear() {
        for (const e of this.#entries.values())
            e.controller?.abort();
        this.#entries.clear();
        this.#inflight.clear();
        for (const key of this.#listeners.keys())
            this.#emit(key);
    }
    /** 특정 접두사만 무효화. 예: 방문을 남긴 뒤 여권 관련 캐시만. */
    invalidate(prefix) {
        const p = JSON.stringify(prefix).slice(0, -1);
        for (const key of [...this.#entries.keys()]) {
            if (key.startsWith(p)) {
                this.#entries.get(key)?.controller?.abort();
                this.#entries.delete(key);
                this.#inflight.delete(key);
                this.#emit(key);
            }
        }
    }
}
//# sourceMappingURL=store.js.map